import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Rocket,
  Save,
  Globe,
  Code,
  MessageSquare,
  AlertCircle,
  Check,
  RefreshCw,
  Calendar,
  Palette,
  Monitor,
  Type,
  Layout,
  Play,
  Square,
  Link as LinkIcon,
  Copy,
  Clock,
  Shield
} from 'lucide-react';
import {DeploymentDto, chatAgentService} from './ChatIntents/chatAgentService';

// Type Definitions
export type Theme = 'dark' | 'light';
export type ChatbotType = 'domain' | 'url' | 'embed';
export type DeploymentStatus = 'active' | 'stopped';
export type EmbedMethod = 'javascript' | 'iframe';

export interface DeploymentSettings {
  id?: string;
  deploymentName: string;
  chatbotType: ChatbotType;
  deploymentUrl: string;
  embedCode: string;
  status: DeploymentStatus;
  
  // Appearance
  headingText: string;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  
  // Access Control
  accessDays: string[];
  accessStartTime: string;
  accessEndTime: string;
  timezone: string;
  
  // Welcome Message
  welcomeMessage: string;
  
  // Domain Path
  domainPath: string;
  
  // Security
  chatToken: string;
  embedToken: string;
  
  // Embed Method
  embedMethod: EmbedMethod;
  /** Required when deployment_type is WebsiteEmbed */
  websiteEmbedOrigin?: string;
}

export interface ChatAgentDeploymentProps {
  theme?: Theme;
  agentId: string;
  tenantId: string;
}

// Theme Configuration
const THEME_CONFIG = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    input: 'bg-neutral-700 border-neutral-600 text-white',
    hover: 'hover:bg-neutral-700'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-50',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-700',
      muted: 'text-gray-500'
    },
    accent: 'bg-indigo-600',
    input: 'bg-white border-gray-300 text-gray-900',
    hover: 'hover:bg-gray-100'
  }
};

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' }
];

// API expects exactly: Mon, Tue, Wed, Thu, Fri, Sat, Sun (3-letter title-case)
const DAY_VALUE_TO_API: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
  Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun'
};
const API_TO_DAY_VALUE: Record<string, string> = {
  Mon: 'monday', Tue: 'tuesday', Wed: 'wednesday', Thu: 'thursday',
  Fri: 'friday', Sat: 'saturday', Sun: 'sunday'
};
const ALLOWED_ACTIVE_DAYS_API = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function normalizeActiveDaysForApi(accessDays: string[]): string[] {
  const out: string[] = [];
  for (const d of accessDays) {
    const api = DAY_VALUE_TO_API[d];
    if (api && !out.includes(api)) out.push(api);
  }
  return out.sort((a, b) => ALLOWED_ACTIVE_DAYS_API.indexOf(a) - ALLOWED_ACTIVE_DAYS_API.indexOf(b));
}

function normalizeActiveDaysFromApi(activeDays: string[] | undefined): string[] {
  if (!Array.isArray(activeDays)) return [];
  const internalValues = DAYS_OF_WEEK.map(x => x.value);
  return activeDays
    .map(d => API_TO_DAY_VALUE[d] ?? (internalValues.includes(d) ? d : null))
    .filter((d): d is string => !!d)
    .filter((d, i, arr) => arr.indexOf(d) === i);
}

// API expects "HH:mm:ss". HTML time input gives "HH:mm".
function normalizeTimeToHHmmss(t: string | undefined): string {
  if (!t || typeof t !== 'string') return '00:00:00';
  const parts = t.trim().split(':');
  const h = parts[0]?.padStart(2, '0') || '00';
  const m = parts[1]?.padStart(2, '0') || '00';
  const s = parts[2]?.padStart(2, '0') ?? '00';
  return `${h}:${m}:${s}`;
}

const CHATBOT_TYPES = [
  { value: 'domain', label: 'Custom Domain', icon: Globe, description: 'Deploy on your own domain', disabled: true },
  { value: 'url', label: 'Direct URL', icon: LinkIcon, description: 'Shareable chatbot link', disabled: false },
  { value: 'embed', label: 'Website Embed', icon: Code, description: 'Integrate into your site', disabled: true }
];

// API expects: DirectURL | CustomDomain | WebsiteEmbed
const DEPLOYMENT_TYPE_TO_API: Record<ChatbotType, string> = {
  url: 'DirectURL',
  domain: 'CustomDomain',
  embed: 'WebsiteEmbed'
};
const DEPLOYMENT_TYPE_FROM_API: Record<string, ChatbotType> = {
  DirectURL: 'url',
  CustomDomain: 'domain',
  WebsiteEmbed: 'embed'
};

const POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' }
];

// Compact Section Header Component
const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  theme: Theme;
}> = ({ icon: Icon, title, theme }) => {
  const colors = THEME_CONFIG[theme];
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className={`p-1.5 ${colors.accent} rounded-lg`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <h3 className={`text-sm font-semibold ${colors.text.primary}`}>
        {title}
      </h3>
    </div>
  );
};

// Main Deployment Component
const ChatAgentDeployment: React.FC<ChatAgentDeploymentProps> = ({
  theme = 'dark',
  agentId,
  tenantId
}) => {
  const colors = THEME_CONFIG[theme];
  const { chatbotId } = useParams<{ chatbotId?: string }>();
  const masterId = chatbotId || agentId;

  const [settings, setSettings] = useState<DeploymentSettings>({
    deploymentName: '',
    chatbotType: 'url',
    deploymentUrl: '',
    embedCode: '',
    status: 'stopped',
    
    headingText: 'Chat with us',
    position: 'bottom-right',
    
    accessDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    accessStartTime: '09:00',
    accessEndTime: '17:00',
    timezone: 'UTC',
    
    welcomeMessage: 'Hello! How can I help you today?',
    
    domainPath: '',
    chatToken: '',
    embedToken: '',
    embedMethod: 'javascript'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deploymentNameError, setDeploymentNameError] = useState<string | null>(null);

  useEffect(() => {
    if (masterId) loadDeployment();
    else setLoading(false);
  }, [masterId]);

  const generateToken = (type: string): string => {
    const prefix = type === 'chat' ? 'ctk_' : 'etk_';
    const randomString = Math.random().toString(36).substring(2, 15) + 
                        Math.random().toString(36).substring(2, 15);
    return prefix + randomString;
  };

  const loadDeployment = async () => {
    if (!masterId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await chatAgentService.getDeploymentsByAgent(masterId);
      
      const list = Array.isArray(response?.data) ? response.data : (response?.data?.data ?? []);
      if (response?.success === true && list.length > 0) {
        const d = list[0];
        const id = d.id ?? d.deployment_id;
        const chatToken = d.chat_token || generateToken('chat');
        const embedToken = d.embed_token || generateToken('embed');
        const headingText = typeof d.heading_text === 'string' ? d.heading_text : (d.heading_text?.en ?? 'Chat with us');
        const welcomeMessage = typeof d.welcome_message === 'string' ? d.welcome_message : (d.welcome_message?.en ?? '');

        setSettings({
          id,
          deploymentName: d.deployment_name || '',
          chatbotType: (d.deployment_type ? DEPLOYMENT_TYPE_FROM_API[d.deployment_type] : undefined) ?? (d.chatbot_type as DeploymentSettings['chatbotType']) ?? 'url',
          deploymentUrl: d.deployment_url || d.direct_url || d.custom_domain || '',
          embedCode: d.embed_code || '',
          status: (d.status || 'stopped') as DeploymentSettings['status'],
          headingText,
          position: (d.position || 'bottom-right') as DeploymentSettings['position'],
          accessDays: normalizeActiveDaysFromApi(d.active_days || d.access_days),
          accessStartTime: (d.active_start_time || d.access_start_time || '09:00').slice(0, 5),
          accessEndTime: (d.active_end_time || d.access_end_time || '17:00').slice(0, 5),
          timezone: d.timezone || 'UTC',
          welcomeMessage,
          domainPath: d.url_path || d.domain_path || '',
          chatToken,
          embedToken,
          embedMethod: (d.embed_method || 'javascript') as DeploymentSettings['embedMethod']
        });
      } else {
        setSettings(prev => ({
          ...prev,
          chatToken: generateToken('chat'),
          embedToken: generateToken('embed')
        }));
      }
    } catch (err) {
      console.error('Error loading deployment:', err);
      setError('Failed to load deployment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setDeploymentNameError(null);
    setSuccessMessage(null);

    const nameTrimmed = settings.deploymentName?.trim();
    if (!nameTrimmed) {
      setDeploymentNameError('Deployment name is required.');
      setError('Please enter a deployment name. This field is required to save.');
      return;
    }

    const startTime = normalizeTimeToHHmmss(settings.accessStartTime);
    const endTime = normalizeTimeToHHmmss(settings.accessEndTime);
    if (startTime >= endTime) {
      setError('Active start time must be earlier than active end time.');
      return;
    }

    const deploymentType = DEPLOYMENT_TYPE_TO_API[settings.chatbotType];
    if (settings.chatbotType === 'domain') {
      const customDomain = settings.deploymentUrl?.trim();
      if (!customDomain) {
        setError('Custom domain is required for Custom Domain deployment.');
        return;
      }
    }
    if (settings.chatbotType === 'embed') {
      const origin = settings.websiteEmbedOrigin?.trim();
      if (!origin) {
        setError('Website embed origin is required for Website Embed deployment.');
        return;
      }
    }

    setSaving(true);

    try {
      let deploymentUrl = settings.deploymentUrl;
      let embedCode = settings.embedCode;

      if (settings.chatbotType === 'url') {
        const path = masterId;
        deploymentUrl = `https://selficai.com/enterprisechat/${path}`;
      } else if (settings.chatbotType === 'domain') {
        deploymentUrl = settings.deploymentUrl;
      }

      if (settings.chatbotType === 'embed') {
        if (settings.embedMethod === 'javascript') {
          embedCode = `<!-- Selfic AI Chatbot - JavaScript Integration -->
<script>
  window.selficConfig = {
    agentId: "${masterId}",
    embedToken: "${settings.embedToken}",
    position: "${settings.position}",
    heading: "${settings.headingText}",
    welcomeMessage: "${settings.welcomeMessage}"
  };
</script>
<script src="https://cdn.selficai.com/chatbot.js" async></script>`;
        } else {
          const iframeUrl = `https://selficai.com/embed/${masterId}?token=${settings.embedToken}&position=${settings.position}`;
          embedCode = `<!-- Selfic AI Chatbot - iFrame Integration -->
<iframe 
  src="${iframeUrl}"
  width="100%"
  height="600px"
  frameborder="0"
  allow="microphone; camera"
  style="border: none; border-radius: 8px;"
></iframe>`;
        }
      }

      const basePayload: DeploymentDto = {

        id: masterId,
        deployment_id: settings.id,
        chatagent_master_id: masterId,
        agent_id: masterId,
        tenant_id: tenantId,
        deployment_name: settings.deploymentName,
        environment: 'production',
        status: settings.status,
        deployment_type: deploymentType,
        active_days: normalizeActiveDaysForApi(settings.accessDays),
        active_start_time: startTime,
        active_end_time: endTime,
        timezone: settings.timezone?.trim() || 'UTC',
        heading_text: settings.headingText,
        welcome_message: settings.welcomeMessage,
        chat_token: settings.chatToken,
        deployed_by: localStorage.getItem('user_uid') || 'system'
      };

      if (settings.chatbotType === 'url') {
        basePayload.deployment_url = deploymentUrl;
        basePayload.direct_url = deploymentUrl;
        basePayload.base_domain = 'selficai.com';
        basePayload.url_path = masterId.trim() || undefined;
      } else if (settings.chatbotType === 'domain') {
        basePayload.custom_domain = settings.deploymentUrl?.trim();
        basePayload.deployment_url = settings.deploymentUrl?.trim();
      } else if (settings.chatbotType === 'embed') {
        basePayload.website_embed_origin = settings.websiteEmbedOrigin?.trim();
      }

      const response = await chatAgentService.upsertDeployment(basePayload);

      if (response?.success === true) {
        setSuccessMessage('Deployment saved successfully');
        setSettings(prev => ({
          ...prev,
          deploymentUrl,
          embedCode
        }));
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err: any) {
      console.error('Error saving deployment:', err);
      setError(err?.response?.data?.message || 'Failed to save deployment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof DeploymentSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    if (field === 'deploymentName') setDeploymentNameError(null);
    if (field === 'deploymentName') setError(null);
  };

  const toggleDay = (day: string) => {
    setSettings(prev => ({
      ...prev,
      accessDays: prev.accessDays.includes(day)
        ? prev.accessDays.filter(d => d !== day)
        : [...prev.accessDays, day]
    }));
  };

  const regenerateToken = (type: 'chat' | 'embed') => {
    const newToken = generateToken(type);
    if (type === 'chat') {
      setSettings(prev => ({ ...prev, chatToken: newToken }));
    } else {
      setSettings(prev => ({ ...prev, embedToken: newToken }));
    }
    setSuccessMessage(`${type === 'chat' ? 'Chat' : 'Embed'} token regenerated`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setSuccessMessage(`${type} copied!`);
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  if (loading) {
    return (
      <div className={`h-full ${colors.background} flex items-center justify-center`}>
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
          <p className={`text-sm ${colors.text.secondary}`}>Loading deployment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${colors.background} overflow-auto`}>
      {/* Compact Header */}
      <div className={`px-5 py-3 border-b ${colors.border} ${colors.primary} sticky top-0 z-10 backdrop-blur-sm bg-opacity-95`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-base font-semibold ${colors.text.primary}`}>Deployment Settings</h1>
            <p className={`text-xs ${colors.text.muted} mt-0.5`}>
              Configure and deploy your chatbot
            </p>
          </div>

          <div className="flex items-center gap-2">
            {successMessage && (
              <div className="flex items-center gap-1.5 text-green-500 text-xs font-medium">
                <Check className="w-3.5 h-3.5" />
                {successMessage}
              </div>
            )}

            <button
              onClick={loadDeployment}
              className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} text-xs rounded transition-colors flex items-center gap-1.5`}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={handleSave}
              disabled={saving || !settings.deploymentName?.trim()}
              className={`px-3 py-1.5 ${colors.accent} hover:bg-indigo-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-xs rounded transition-colors flex items-center gap-1.5 font-medium`}
              title={!settings.deploymentName?.trim() ? 'Deployment name is required' : undefined}
            >
              {saving ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save & Deploy
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 max-w-7xl mx-auto">
        
        {/* Error Message */}
        {error && (
          <div className={`mb-4 p-3 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-lg flex items-start gap-2`}>
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-xs font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className={`mb-4 p-3 ${theme === 'light' ? 'bg-green-50 border-green-200' : 'bg-green-900/20 border-green-700'} border rounded-lg flex items-start gap-2`}>
            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-green-800' : 'text-green-200'}`}>
                {successMessage}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* Left Column - Main Settings */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Basic Configuration */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Rocket} title="Basic Configuration" theme={theme} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Deployment Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={settings.deploymentName}
                    onChange={(e) => handleInputChange('deploymentName', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      deploymentNameError ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    placeholder="Production Deployment"
                    aria-invalid={!!deploymentNameError}
                    aria-describedby={deploymentNameError ? 'deployment-name-error' : undefined}
                  />
                  {deploymentNameError && (
                    <p id="deployment-name-error" className="mt-1 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {deploymentNameError}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Status
                  </label>
                  <button
                    onClick={() => handleInputChange('status', settings.status === 'active' ? 'stopped' : 'active')}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      settings.status === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                  >
                    {settings.status === 'active' ? (
                      <>
                        <Play className="w-3.5 h-3.5" />
                        Active
                      </>
                    ) : (
                      <>
                        <Square className="w-3.5 h-3.5" />
                        Stopped
                      </>
                    )}
                  </button>
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                    Deployment Type
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {CHATBOT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isDisabled = type.disabled;
                      const isSelected = settings.chatbotType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => !isDisabled && handleInputChange('chatbotType', type.value)}
                          className={`p-2.5 border rounded-lg text-left transition-all ${
                            isDisabled
                              ? 'opacity-50 cursor-not-allowed border-neutral-600 bg-neutral-800/50'
                              : isSelected
                              ? `border-indigo-500 ${colors.accent} bg-opacity-10`
                              : `${colors.border} ${colors.secondary} ${colors.hover}`
                          }`}
                          title={isDisabled ? 'Coming soon' : type.description}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Icon className={`w-4 h-4 ${isSelected ? 'text-indigo-400' : colors.text.muted}`} />
                            <span className={`text-xs font-medium ${colors.text.primary}`}>
                              {type.label}
                            </span>
                          </div>
                          <p className={`text-[10px] ${colors.text.muted}`}>
                            {isDisabled ? 'Coming soon' : type.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {settings.chatbotType === 'domain' && (
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      Custom Domain
                    </label>
                    <input
                      type="text"
                      value={settings.deploymentUrl}
                      onChange={(e) => handleInputChange('deploymentUrl', e.target.value)}
                      className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="chat.yourdomain.com"
                    />
                  </div>
                )}

                {settings.chatbotType === 'url' && (
                  <div className="md:col-span-2">
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      URL Path
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} rounded text-sm ${colors.text.muted} whitespace-nowrap`}>
                        chat.selficai.com/enterprisechat/
                      </span>
                      <input
                        type="text"
                        value={masterId}
                        disabled
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-sm opacity-70 cursor-not-allowed`}
                        placeholder="agent guid"
                      />
                    </div>
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Auto-filled from URL route agent GUID
                    </p>
                  </div>
                )}

              </div>
            </div>

            {/* Appearance Settings */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Palette} title="Appearance" theme={theme} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div className="md:col-span-2">
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Heading Text
                  </label>
                  <input
                    type="text"
                    value={settings.headingText}
                    onChange={(e) => handleInputChange('headingText', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="Chat with us"
                  />
                </div>

                {settings.chatbotType === 'embed' && (
                  <>
                    <div className="md:col-span-2">
                      <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                        Embedding Method
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleInputChange('embedMethod', 'javascript')}
                          className={`p-2.5 border rounded-lg text-left transition-all ${
                            settings.embedMethod === 'javascript'
                              ? `border-indigo-500 ${colors.accent} bg-opacity-10`
                              : `${colors.border} ${colors.secondary} ${colors.hover}`
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Code className={`w-4 h-4 ${settings.embedMethod === 'javascript' ? 'text-indigo-400' : colors.text.muted}`} />
                            <span className={`text-xs font-medium ${colors.text.primary}`}>
                              JavaScript
                            </span>
                          </div>
                          <p className={`text-[10px] ${colors.text.muted}`}>Floating widget</p>
                        </button>
                        
                        <button
                          onClick={() => handleInputChange('embedMethod', 'iframe')}
                          className={`p-2.5 border rounded-lg text-left transition-all ${
                            settings.embedMethod === 'iframe'
                              ? `border-indigo-500 ${colors.accent} bg-opacity-10`
                              : `${colors.border} ${colors.secondary} ${colors.hover}`
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <Layout className={`w-4 h-4 ${settings.embedMethod === 'iframe' ? 'text-indigo-400' : colors.text.muted}`} />
                            <span className={`text-xs font-medium ${colors.text.primary}`}>
                              iFrame
                            </span>
                          </div>
                          <p className={`text-[10px] ${colors.text.muted}`}>Inline embed</p>
                        </button>
                      </div>
                    </div>

                    {settings.embedMethod === 'javascript' && (
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                          Widget Position
                        </label>
                        <select
                          value={settings.position}
                          onChange={(e) => handleInputChange('position', e.target.value)}
                          className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                        >
                          {POSITION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>

            {/* Welcome Message */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Type} title="Welcome Message" theme={theme} />

              <div>
                <textarea
                  value={settings.welcomeMessage}
                  onChange={(e) => handleInputChange('welcomeMessage', e.target.value)}
                  className={`w-full ${colors.input} border rounded px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none`}
                  rows={3}
                  placeholder="Hello! How can I help you today?"
                />
              </div>
            </div>

            {/* Security Tokens */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Shield} title="Security Tokens" theme={theme} />

              <div className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Chat Token
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={settings.chatToken}
                      readOnly
                      className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs font-mono`}
                    />
                    <button
                      onClick={() => copyToClipboard(settings.chatToken, 'Token')}
                      className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                      title="Copy"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => regenerateToken('chat')}
                      className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                      title="Regenerate"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                    Required for API authentication
                  </p>
                </div>

                {settings.chatbotType === 'embed' && (
                  <div>
                    <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                      Embed Token
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.embedToken}
                        readOnly
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs font-mono`}
                      />
                      <button
                        onClick={() => copyToClipboard(settings.embedToken, 'Token')}
                        className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                        title="Copy"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => regenerateToken('embed')}
                        className={`px-2.5 py-1.5 ${colors.secondary} border ${colors.border} ${colors.hover} rounded`}
                        title="Regenerate"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                      Required for website embedding
                    </p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column - Access Control & Deployment Info */}
          <div className="space-y-4">
            
            {/* Access Schedule */}
            <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
              <SectionHeader icon={Calendar} title="Access Schedule" theme={theme} />

              <div className="space-y-3">
                
                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-2`}>
                    Active Days
                  </label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.value}
                        onClick={() => toggleDay(day.value)}
                        className={`px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
                          settings.accessDays.includes(day.value)
                            ? `${colors.accent} text-white`
                            : `${colors.secondary} ${colors.text.secondary} ${colors.hover}`
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Active Hours
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-[10px] ${colors.text.muted} mb-1`}>Start</label>
                      <input
                        type="time"
                        value={settings.accessStartTime}
                        onChange={(e) => handleInputChange('accessStartTime', e.target.value)}
                        className={`w-full ${colors.input} border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                    <div>
                      <label className={`block text-[10px] ${colors.text.muted} mb-1`}>End</label>
                      <input
                        type="time"
                        value={settings.accessEndTime}
                        onChange={(e) => handleInputChange('accessEndTime', e.target.value)}
                        className={`w-full ${colors.input} border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className={`block text-xs font-medium ${colors.text.primary} mb-1.5`}>
                    Timezone
                  </label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => handleInputChange('timezone', e.target.value)}
                    className={`w-full ${colors.input} border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Asia/Riyadh">Riyadh (AST)</option>
                  </select>
                </div>

              </div>
            </div>

            {/* Deployment Info - Direct URL */}
            {settings.chatbotType === 'url' && settings.deploymentUrl && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={LinkIcon} title="Direct URL Access" theme={theme} />

                <div className="space-y-3">
                  <div>
                    <label className={`block text-[10px] ${colors.text.muted} mb-1`}>
                      Shareable Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.deploymentUrl}
                        readOnly
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs`}
                      />
                      <button
                        onClick={() => copyToClipboard(settings.deploymentUrl, 'URL')}
                        className={`px-2.5 py-1.5 ${colors.accent} hover:bg-indigo-700 text-white rounded`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                      Quick Start Guide
                    </h4>
                    <ol className={`text-[10px] ${colors.text.secondary} space-y-1.5 list-decimal list-inside`}>
                      <li>Share the URL with your users</li>
                      <li>Users can access the chatbot directly in their browser</li>
                      <li>Use the chat token for API integration</li>
                      <li>Monitor conversations in the dashboard</li>
                    </ol>
                  </div>

                  <div className={`p-3 ${theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-700'} border rounded-lg`}>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className={`text-[10px] ${theme === 'light' ? 'text-blue-800' : 'text-blue-200'}`}>
                        The chatbot is accessible 24/7 unless restricted by the access schedule above.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deployment Info - Embed Code */}
            {settings.chatbotType === 'embed' && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={Code} title="Integration Code" theme={theme} />

                <div className="space-y-3">
                  
                  {/* Method Tabs */}
                  <div className="flex gap-2 p-1 bg-neutral-800/50 rounded-lg">
                    <button
                      onClick={() => handleInputChange('embedMethod', 'javascript')}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        settings.embedMethod === 'javascript'
                          ? `${colors.accent} text-white shadow-sm`
                          : `${colors.text.secondary} hover:text-white`
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Code className="w-3.5 h-3.5" />
                        JavaScript
                      </div>
                    </button>
                    <button
                      onClick={() => handleInputChange('embedMethod', 'iframe')}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-all ${
                        settings.embedMethod === 'iframe'
                          ? `${colors.accent} text-white shadow-sm`
                          : `${colors.text.secondary} hover:text-white`
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <Layout className="w-3.5 h-3.5" />
                        iFrame
                      </div>
                    </button>
                  </div>

                  {/* Code Display */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className={`text-[10px] font-medium ${colors.text.muted}`}>
                        {settings.embedMethod === 'javascript' ? 'Add this code before </body>' : 'Add this code where you want the chatbot'}
                      </label>
                      <button
                        onClick={() => copyToClipboard(settings.embedCode, 'Code')}
                        className={`px-2 py-1 ${colors.accent} hover:bg-indigo-700 text-white rounded text-[10px] flex items-center gap-1`}
                      >
                        <Copy className="w-3 h-3" />
                        Copy Code
                      </button>
                    </div>
                    <div className="relative">
                      <pre className={`${colors.input} border rounded-lg p-3 text-[10px] font-mono overflow-x-auto whitespace-pre-wrap`}>
                        <code>{settings.embedCode}</code>
                      </pre>
                    </div>
                  </div>

                  {/* Installation Guide */}
                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2 flex items-center gap-1.5`}>
                      <div className={`w-5 h-5 rounded-full ${colors.accent} flex items-center justify-center text-white text-[10px] font-bold`}>
                        {settings.embedMethod === 'javascript' ? '📝' : '🖼️'}
                      </div>
                      How to Install
                    </h4>
                    {settings.embedMethod === 'javascript' ? (
                      <div className={`text-[10px] ${colors.text.secondary} space-y-2`}>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>1</span>
                          <p>Open your website's HTML file</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>2</span>
                          <p>Find the closing <code className={`${colors.input} px-1 rounded`}>&lt;/body&gt;</code> tag (usually at the bottom)</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>3</span>
                          <p>Paste the code right before it</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>4</span>
                          <p>Save and refresh your website - the chatbot will appear in the {settings.position.replace('-', ' ')} corner</p>
                        </div>
                      </div>
                    ) : (
                      <div className={`text-[10px] ${colors.text.secondary} space-y-2`}>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>1</span>
                          <p>Copy the iFrame code above</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>2</span>
                          <p>Paste it anywhere in your HTML where you want the chatbot to appear</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>3</span>
                          <p>Adjust <code className={`${colors.input} px-1 rounded`}>width</code> and <code className={`${colors.input} px-1 rounded`}>height</code> as needed</p>
                        </div>
                        <div className="flex gap-2">
                          <span className={`flex-shrink-0 w-4 h-4 rounded-full ${colors.accent} text-white flex items-center justify-center text-[9px] font-bold mt-0.5`}>4</span>
                          <p>The chatbot will be embedded inline with your content</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Features/Benefits */}
                  {settings.embedMethod === 'javascript' && (
                    <div className={`p-3 ${theme === 'light' ? 'bg-green-50 border-green-200' : 'bg-green-900/20 border-green-700'} border rounded-lg`}>
                      <div className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className={`text-[10px] font-medium ${theme === 'light' ? 'text-green-900' : 'text-green-200'} mb-1`}>
                            ✨ Smart Loading
                          </p>
                          <p className={`text-[10px] ${theme === 'light' ? 'text-green-800' : 'text-green-300'}`}>
                            Loads asynchronously without slowing down your website. Works on all devices and browsers.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {settings.embedMethod === 'iframe' && (
                    <div className={`p-3 ${theme === 'light' ? 'bg-blue-50 border-blue-200' : 'bg-blue-900/20 border-blue-700'} border rounded-lg`}>
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className={`text-[10px] font-medium ${theme === 'light' ? 'text-blue-900' : 'text-blue-200'} mb-1`}>
                            💡 Tip
                          </p>
                          <p className={`text-[10px] ${theme === 'light' ? 'text-blue-800' : 'text-blue-300'}`}>
                            Use <code className={`${colors.input} px-1 rounded`}>width="100%"</code> for responsive sizing. Recommended minimum height: 500px.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Security Warning */}
                  <div className={`p-3 ${theme === 'light' ? 'bg-amber-50 border-amber-200' : 'bg-amber-900/20 border-amber-700'} border rounded-lg`}>
                    <div className="flex items-start gap-2">
                      <Shield className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className={`text-[10px] font-medium ${theme === 'light' ? 'text-amber-900' : 'text-amber-200'} mb-1`}>
                          🔒 Security
                        </p>
                        <p className={`text-[10px] ${theme === 'light' ? 'text-amber-800' : 'text-amber-300'}`}>
                          Your embed token is included in the code. Don't share it publicly. If compromised, regenerate it above.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Example HTML Structure */}
                  {settings.embedMethod === 'javascript' && (
                    <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                      <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                        📄 Example HTML Structure
                      </h4>
                      <pre className={`${colors.input} rounded p-2 text-[9px] font-mono overflow-x-auto whitespace-pre`}>
                        <code>{`<!DOCTYPE html>
<html>
<head>
    <title>My Website</title>
</head>
<body>
    <!-- Your website content -->
    
    <!-- Selfic AI Chatbot -->
    <script>
      window.selficConfig = {...};
    </script>
    <script src="https://cdn.selficai.com/chatbot.js" async></script>
</body>
</html>`}</code>
                      </pre>
                    </div>
                  )}

                </div>
              </div>
            )}

            {/* Deployment Info - Domain */}
            {settings.chatbotType === 'domain' && settings.deploymentUrl && (
              <div className={`${colors.surface} rounded-lg p-4 border ${colors.border}`}>
                <SectionHeader icon={Globe} title="Domain Deployment" theme={theme} />

                <div className="space-y-3">
                  <div>
                    <label className={`block text-[10px] ${colors.text.muted} mb-1`}>
                      Custom Domain
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={settings.deploymentUrl}
                        readOnly
                        className={`flex-1 ${colors.input} border rounded px-2.5 py-1.5 text-xs`}
                      />
                      <button
                        onClick={() => copyToClipboard(settings.deploymentUrl, 'Domain')}
                        className={`px-2.5 py-1.5 ${colors.accent} hover:bg-indigo-700 text-white rounded`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className={`p-3 ${colors.secondary} border ${colors.border} rounded-lg`}>
                    <h4 className={`text-xs font-semibold ${colors.text.primary} mb-2`}>
                      DNS Configuration
                    </h4>
                    <div className={`text-[10px] ${colors.text.secondary} space-y-1.5`}>
                      <p>Add a CNAME record pointing to:</p>
                      <code className={`block ${colors.input} px-2 py-1 rounded mt-1`}>
                        chatbot.selficai.com
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAgentDeployment;